import axios from 'axios';

// Axios インスタンス設定（main.tsxで設定されたbaseURLを使用）
const apiClient = axios.create({
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// エラーハンドリング用のインターセプター
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API エラー:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// 型定義
export interface SystemStats {
  overview: {
    total_users: number;
    active_users_today: number;
    new_registrations_today: number;
    total_companies: number;
    pending_company_approvals: number;
    system_health_score: number;
  };
  user_metrics: {
    total_students: number;
    active_students: number;
    new_student_registrations: number;
    retention_rate: number;
  };
  company_metrics: {
    total_companies: number;
    approved_companies: number;
    pending_approvals: number;
    active_recruiters: number;
  };
  engagement_metrics: {
    total_scout_messages: number;
    scout_response_rate: number;
    timeline_posts: number;
    chat_sessions_today: number;
  };
}

export interface UserInfo {
  id: number;
  email: string;
  full_name: string;
  user_role: string;
  status: string; // ユーザーステータス
  university?: string;
  registration_date: string;
  last_login?: string;
  violation_count: number;
  activity_score: number;
  deletedAt?: string; // 退会日時（キャメルケース）
  deleted_at?: string; // 退会日時（スネークケース、後方互換性）
}

export interface CompanyInfo {
  id: number;
  company_name: string;
  representative_name: string;
  business_type: string;
  employee_count_range: string;
  prefecture: string;
  city: string;
  contact_email: string;
  status: string;
  registration_date: string;
  approved_at?: string;
  employees: CompanyEmployeeInfo[];
  platform_activity: {
    scout_messages_sent: number;
    timeline_posts_created: number;
    total_employees: number;
    last_activity_date?: string;
  };
  is_sponsor?: boolean;
  employee_count?: number;
  scout_messages_sent?: number;
  deleted_at?: string; // 退会日時
}

export interface CompanyEmployeeInfo {
  user_id: number;
  full_name: string;
  email: string;
  role: string;
  created_at: string;
  last_login_at?: string;
  is_active: boolean;
}

export interface ActivityRecord {
  id: number;
  activity_type: string;
  description: string;
  timestamp: string;
  metadata?: any;
}

export interface AdminActionLog {
  id: number;
  admin_user_id: number;
  action_type: string;
  target_type: string;
  target_id: number;
  description: string;
  reason?: string;
  timestamp: string;
  ip_address?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

export interface CompanyListResponse {
  companies: CompanyInfo[];
  pagination: {
    total_count: number;
    total_pages: number;
    current_page: number;
    per_page: number;
  };
}

export interface UserSearchFilters {
  name?: string;
  email?: string;
  university?: string;
  user_type?: string;
  status?: string;
  registration_date_from?: string;
  registration_date_to?: string;
  has_violations?: boolean;
  sort?: string;
  order?: string;
  page?: number;
  limit?: number;
}

export interface CompanySearchFilters {
  company_name?: string;
  business_type?: string;
  prefecture?: string;
  status?: string;
  is_sponsor?: boolean | string;
  registration_date_from?: string;
  registration_date_to?: string;
  page?: number;
  limit?: number;
}

export interface UpdateUserStatusRequest {
  action: 'suspend' | 'activate' | 'verify' | 'flag' | 'unflag';
  reason: string;
  duration_days?: number;
  notify_user?: boolean;
  internal_notes?: string;
}

export interface CompanyApprovalRequest {
  action: 'approve' | 'reject' | 'request_more_info';
  reason?: string;
  required_documents?: string[];
  approval_conditions?: string[];
  notify_company?: boolean;
}

// 管理者向け学生・企業管理 API Service
export const adminManagementService = {
  // システム統計ダッシュボード取得
  async getDashboard(): Promise<SystemStats> {
    const response = await apiClient.get('/api/admin/dashboard');
    return response.data.data;
  },

  // 学生一覧・検索
  async getUsers(
    filters: UserSearchFilters = {}
  ): Promise<PaginatedResponse<UserInfo>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    });

    const response = await apiClient.get(
      `/api/admin/users?${params.toString()}`
    );
    return response.data.data;
  },

  // 学生詳細取得
  async getUserDetail(
    userId: number
  ): Promise<UserInfo & { recent_activities: ActivityRecord[] }> {
    const response = await apiClient.get(`/api/admin/users/${userId}`);
    return response.data.data;
  },

  // 学生ステータス更新
  async updateUserStatus(
    userId: number,
    request: UpdateUserStatusRequest
  ): Promise<void> {
    await apiClient.put(`/api/admin/users/${userId}/status`, request);
  },

  // 企業一覧・検索
  async getCompanies(
    filters: CompanySearchFilters = {}
  ): Promise<CompanyListResponse> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    });

    const response = await apiClient.get(
      `/api/admin/companies?${params.toString()}`
    );

    const data = response.data.data;

    // バックエンドのAdminCompanySummary[]をCompanyInfo[]に変換
    const companies: CompanyInfo[] = data.companies.map((company: any) => ({
      id: company.id,
      company_name: company.company_name,
      representative_name: company.representative_name,
      business_type: company.business_type,
      employee_count_range: company.employee_count_range,
      prefecture: company.prefecture,
      city: '', // バックエンドレスポンスにcityがない場合
      contact_email: '', // バックエンドレスポンスにcontact_emailがない場合
      status: company.status,
      registration_date: company.registration_date,
      approved_at: company.approved_at,
      employees: [],
      platform_activity: {
        scout_messages_sent: company.scout_messages_sent || 0,
        timeline_posts_created: 0,
        total_employees: company.employee_count || 0,
        last_activity_date: undefined,
      },
      is_sponsor: company.is_sponsor,
      employee_count: company.employee_count,
      scout_messages_sent: company.scout_messages_sent,
      deleted_at: company.deleted_at, // 退会日時を追加
    }));

    return {
      companies,
      pagination: data.pagination,
    };
  },

  // 企業詳細取得
  async getCompanyDetail(companyId: number): Promise<CompanyInfo> {
    const response = await apiClient.get(`/api/admin/companies/${companyId}`);
    const data = response.data.data;

    // 従業員データを変換
    const employees: CompanyEmployeeInfo[] = (data.employees || []).map(
      (emp: any) => ({
        user_id: emp.user_id,
        full_name: emp.full_name,
        email: emp.email,
        role: emp.role,
        last_login_at: emp.last_login_at,
        is_active: emp.status === 'active',
      })
    );

    // バックエンドのレスポンス構造をフロントエンドの型に変換
    return {
      id: data.company.id,
      company_name: data.company.company_name,
      representative_name: data.company.representative_name,
      business_type: data.company.business_type,
      employee_count_range: data.company.employee_count_range,
      prefecture: data.company.prefecture,
      city: data.company.city,
      contact_email: data.company.contact_email,
      status: data.company.status,
      registration_date: data.company.created_at,
      approved_at: data.company.approved_at,
      employees: employees,
      platform_activity: {
        scout_messages_sent: data.activity_stats?.total_scout_messages || 0,
        timeline_posts_created: data.activity_stats?.total_timeline_posts || 0,
        total_employees: employees.length,
        last_activity_date: data.activity_stats?.last_activity_date,
      },
      is_sponsor: data.company.is_sponsor || false,
    };
  },

  // 企業承認・拒否
  async updateCompanyApproval(
    companyId: number,
    request: CompanyApprovalRequest
  ): Promise<void> {
    await apiClient.put(`/api/admin/companies/${companyId}/approval`, request);
  },

  // 承認待ち企業一覧
  async getPendingCompanies(): Promise<CompanyInfo[]> {
    const response = await apiClient.get('/api/admin/companies/pending');
    return response.data.data;
  },

  // 管理者アクションログ取得
  async getAdminActions(
    page: number = 1,
    limit: number = 20
  ): Promise<PaginatedResponse<AdminActionLog>> {
    const response = await apiClient.get(
      `/api/admin/actions?page=${page}&limit=${limit}`
    );
    return response.data.data;
  },

  // スポンサー設定更新
  async updateSponsorSettings(
    companyId: number,
    isSponsor: boolean
  ): Promise<void> {
    await apiClient.put(`/api/sponsor/settings/${companyId}`, {
      isSponsor: isSponsor,
    });
  },
};
